import { startCase } from "lodash";
import { Redirect, useHistory } from "react-router";

import {
  Alert,
  Button,
  Icon,
  Link,
  Text,
  toastSuccess,
} from "@alpacahq/ui/old";

import { useGetCurrencyCloudAccount } from "~/v2/api/hooks";
import { FullScreenSpinner } from "~/v2/components/common";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { FAQ } from "../../../../../components/common/FAQ";
import { Layout } from "../../common/Layout";
import { CURRENCYCLOUD_DEPOSIT_FAQ } from "../../link/currencyCloud";

const CC_KEYS = [
  "account_number",
  "account_holder_name",
  "routing_code",
  "routing_code_type",
  "bank_name",
  "bank_address",
];

const CurrencyCloudDepositPage = () => {
  const history = useHistory();

  const { currentAccount } = useAccountContext({ liveOnly: true });

  // IRA accounts cannot deposit via currencyCloud
  const isIRAAccount = currentAccount?.account_type === "ira";

  const { data: cc, isLoading } = useGetCurrencyCloudAccount({
    accountID: currentAccount?.id || "",
    options: {
      enabled: !!currentAccount?.id && !isIRAAccount,
      refetchOnMount: true,
    },
  });

  const instructions = Object.fromEntries(
    CC_KEYS.map((key) => {
      const label = startCase(key);
      const value = cc?.[key as keyof typeof cc] || "";

      return [label, value];
    }),
  );

  const instructionsString = Object.entries(instructions)
    .map(([label, value]) => `${label}: ${value}`)
    .join("\n");

  const onCopyClicked = (text: string) => async () => {
    await navigator.clipboard.writeText(text);

    toastSuccess("Copied to clipboard");
  };

  const onBackClicked = () => history.push(Path.ROUTE_FUNDING_DEPOSIT);

  if (isIRAAccount) {
    return <Redirect to={Path.ROUTE_FUNDING_DEPOSIT} />;
  }

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  return (
    <Layout
      title="Local Currency Transfer"
      subtitle="Deposit Funds via CurrencyCloud"
      faq={<FAQ faq={CURRENCYCLOUD_DEPOSIT_FAQ} />}
    >
      <Button variant="secondary" onClick={onBackClicked} className="mb-8">
        <Icon name="ArrowLeftCircle" className="mr-2" />
        Back to Deposit
      </Button>

      <Text className="pb-4 font-semibold text-xl">
        CurrencyCloud Wire Instructions
      </Text>

      <Alert
        className="mb-4"
        message={
          <span className="flex gap-4">
            <Icon name="InformationCircle" />
            <span>
              For European users, your bank may charge you to send over SWIFT.
              Due to Brexit some banks may charge additional fees, even though
              the European Payment Council has extended the UK's membership to
              SEPA (European rails). For any questions please reach out to{" "}
              <Link href="mailto:support@alpaca.markets">
                support@alpaca.markets
              </Link>
              .
            </span>
          </span>
        }
        variant="info"
      />

      <div className="w-full max-w-lg rounded border border-gray-200 p-4">
        <div className="rounded bg-gray-100 p-4 pb-0">
          {Object.entries(instructions).map(([label, value]) => (
            <div className="flex items-center justify-between pb-4" key={label}>
              <div className="">
                <Text className="color-gray-500">{label}</Text>
                <Text className="font-semibold">{value}</Text>
              </div>
              <Button variant="ghost" onClick={onCopyClicked(value)}>
                <Icon name="ClipboardDocumentList" size="sm" />
              </Button>
            </div>
          ))}
        </div>
        <Button
          className="mt-2 ml-auto"
          variant="alternate"
          onClick={onCopyClicked(instructionsString)}
        >
          Copy All Instructions
          <Icon className="ml-2" name="ClipboardDocumentList" />
        </Button>

        <Text className="py-4">
          Alpaca has partnered with CurrencyCloud to facilitate you funding your
          account faster and cheaper than traditional wire.
        </Text>
        <Text>
          Please make a local transfer from your favorite bank account / mobile
          banking app to the account details above. Your funds will be sent to
          Alpaca's bank account in your country, and as soon as the funds hit
          our account we will credit your Alpaca account with the funds.
        </Text>
        <Text className="py-4">
          Alpaca charges a 1.5% (maximum $40) transaction fee on deposits.
          Minimum deposit equivalent to $2.00.
        </Text>
      </div>
    </Layout>
  );
};

export default CurrencyCloudDepositPage;
